<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm'): void
}>()
const { t } = useI18n()
const dialog = ref<HTMLDialogElement | null>(null)

function syncVisibility() {
  const element = dialog.value
  if (!element) return
  if (props.visible && !element.open) {
    element.showModal()
    element.focus({ preventScroll: true })
  }
  else if (!props.visible && element.open) element.close()
}

function cancel() {
  emit('update:visible', false)
}

function confirm() {
  if (!props.visible) return
  emit('update:visible', false)
  emit('confirm')
}

watch(() => props.visible, syncVisibility, { flush: 'post' })
onMounted(syncVisibility)
onBeforeUnmount(() => dialog.value?.close())
</script>

<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="logout-confirm-dialog"
      aria-labelledby="logout-confirm-title"
      aria-describedby="logout-confirm-description"
      @cancel.prevent="cancel"
      @keydown.stop
    >
      <button
        type="button"
        class="logout-confirm-close"
        :aria-label="t('common.close')"
        @click="cancel"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
      </button>

      <div class="logout-confirm-card">
        <div class="logout-confirm-content">
          <svg class="logout-confirm-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10.5" stroke="currentColor" stroke-width="2" />
            <path d="M12 6.5V12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <circle cx="12" cy="16.5" r="1" fill="currentColor" />
          </svg>
          <div class="logout-confirm-copy">
            <h2 id="logout-confirm-title">{{ t('logoutConfirm.title') }}</h2>
            <p id="logout-confirm-description">{{ t('logoutConfirm.description') }}</p>
          </div>
        </div>
        <div class="logout-confirm-actions">
          <button type="button" class="logout-confirm-cancel" @click="cancel">
            {{ t('common.cancel') }}
          </button>
          <button type="button" class="logout-confirm-submit" @click="confirm">
            {{ t('logoutConfirm.confirm') }}
          </button>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>

<style scoped lang="scss">
.logout-confirm-dialog {
  position: fixed;
  inset: 0;
  width: 508px;
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 32px);
  margin: auto;
  padding: 64px 0 0;
  overflow: visible;
  border: 0;
  color: #fff;
  background: transparent;
  font-family: inherit;
  outline: none;

  &::backdrop {
    background: rgba(0, 0, 0, 0.6);
  }

  &[open] {
    animation: logout-confirm-appear 0.18s ease-out;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-family: inherit;
    font-weight: 400;
    cursor: pointer;
    transition: background 0.18s, color 0.18s;

    &:focus-visible {
      outline: 2px solid #7199ff;
      outline-offset: 3px;
    }
  }
}

.logout-confirm-card {
  padding: 34px 39px 39px;
  max-height: calc(100dvh - 96px);
  overflow-y: auto;
  border: 1px solid #3b3b3d;
  border-radius: 32px;
  background: #272729;
}

.logout-confirm-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.logout-confirm-icon {
  flex: 0 0 24px;
  margin-top: 5px;
  color: #346bff;
}

.logout-confirm-copy {
  min-width: 0;

  h2 {
    margin: 0;
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    line-height: 29px;
  }

  p {
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    line-height: 22px;
  }
}

.logout-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 28px;

  button {
    height: 36px;
    padding: 0 20px;
    border-radius: 20px;
    white-space: nowrap;
  }
}

.logout-confirm-cancel {
  min-width: 88px;
  border: 1px solid #3b3b3d;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
  }
}

.logout-confirm-submit {
  min-width: 103px;
  border: 0;
  background: #346aee;
  color: #fff;
  font-size: 16px;

  &:hover {
    background: #467bff;
  }
}

.logout-confirm-close {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #303032;
  border-radius: 12px;
  background: #222224;
  color: rgba(255, 255, 255, 0.6);

  &:hover {
    background: #303032;
    color: #fff;
  }
}

@keyframes logout-confirm-appear {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 540px) {
  .logout-confirm-card {
    padding: 28px 23px;
    border-radius: 28px;
  }

  .logout-confirm-copy h2 {
    font-size: 21px;
    line-height: 28px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .logout-confirm-dialog[open] {
    animation: none;
  }
}
</style>
